import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { customerCheckout, resolveImageUrl } from '../../services/api';
import WhatsAppAuthModal from '../shared/WhatsAppAuthModal';
import CustomerProfile from '../shared/CustomerProfile';
import {
  ShoppingCartIcon, HeartIcon, MapPinIcon, ChevronRightIcon,
  DevicePhoneMobileIcon, ArrowLeftIcon, BoltIcon, CubeIcon,
  UserGroupIcon, CurrencyRupeeIcon, ShieldCheckIcon, TruckIcon,
  UserIcon, ShoppingBagIcon, ArrowRightOnRectangleIcon, UserCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || '';

const loadRazorpay = () => new Promise(resolve => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true); s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

/* ── Profile Dropdown (identical to Products) ─────────────────────────────── */
const ProfileDropdown = ({ user, onLogout, onViewProfile }) => (
  <>
    <style>{`
      @keyframes pdIn2{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
      .pd2{position:absolute;top:calc(100% + 10px);right:0;background:white;border-radius:1.25rem;
           padding:.4rem;box-shadow:0 16px 40px rgba(0,0,0,.14);border:1px solid #f3f4f6;
           min-width:210px;z-index:200;animation:pdIn2 .2s ease-out;}
      .pd2-hd{padding:.75rem 1rem;border-bottom:1px solid #f3f4f6;margin-bottom:.25rem;}
      .pd2-av{width:2.5rem;height:2.5rem;background:linear-gradient(135deg,#10b981,#059669);
              border-radius:50%;display:flex;align-items:center;justify-content:center;
              color:white;font-size:1rem;font-weight:800;margin-bottom:.4rem;}
      .pd2-nm{font-weight:800;font-size:.92rem;color:#111827;}
      .pd2-ph{font-size:.72rem;color:#6b7280;margin-top:.1rem;}
      .pd2-it{display:flex;align-items:center;gap:.55rem;padding:.55rem .9rem;border-radius:.65rem;
              font-size:.85rem;font-weight:600;color:#374151;cursor:pointer;transition:background .18s;
              border:none;background:none;width:100%;font-family:'Nunito',sans-serif;}
      .pd2-it:hover{background:#f0fdf4;color:#059669;}
      .pd2-it.dng:hover{background:#fff1f2;color:#ef4444;}
      .pd2-it svg{width:.95rem;height:.95rem;flex-shrink:0;}
    `}</style>
    <div className="pd2">
      <div className="pd2-hd">
        <div className="pd2-av">{(user.name||'U').charAt(0).toUpperCase()}</div>
        <div className="pd2-nm">{user.name||'Customer'}</div>
        <div className="pd2-ph">📱 +{user.phone}</div>
      </div>
      <button className="pd2-it" onClick={onViewProfile}><ShoppingBagIcon/>My Orders</button>
      <button className="pd2-it" onClick={onViewProfile}><UserCircleIcon/>My Profile</button>
      <button className="pd2-it dng" onClick={onLogout}><ArrowRightOnRectangleIcon/>Logout</button>
    </div>
  </>
);

/* ── Image helpers ─────────────────────────────────────────────────────────── */
const getImageSrc = (p) => {
  const raw = p?.image_url || p?.imageUrl || p?.image || null;
  return resolveImageUrl(raw);
};
const getEmoji = (p) => {
  const c=(p.category||'').toLowerCase(), n=(p.name||'').toLowerCase();
  if(c.includes('paneer')||n.includes('paneer')) return '🧀';
  if(c.includes('ghee')  ||n.includes('ghee'))   return '🫙';
  if(c.includes('butter')||n.includes('butter')) return '🧈';
  if(c.includes('buttermilk')||n.includes('buttermilk')) return '🧃';
  return '🥛';
};

/* ── Main Checkout ─────────────────────────────────────────────────────────── */
const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart, cartCount } = useCart();
  const { customer: currentUser, isLoggedIn, logout } = useCustomerAuth();

  const [loading,         setLoading]         = useState(false);
  const [scrolled,        setScrolled]        = useState(false);
  const [showAuthModal,   setShowAuthModal]   = useState(false);
  const [showProfile,     setShowProfile]     = useState(false);
  const [showProfileDrop, setShowProfileDrop] = useState(false);
  const [imgErrors,       setImgErrors]       = useState({});
  const currentYear = new Date().getFullYear();

  const [formData, setFormData] = useState({
    name:'', email:'', phone:'', address:'', city:'', pincode:''
  });

  // Pre-fill form from WhatsApp auth customer
  useEffect(() => {
    if (currentUser) {
      setFormData(p => ({
        ...p,
        name:  currentUser.name  || '',
        phone: currentUser.phone || '',
        email: currentUser.email || '',
      }));
    }
  }, [currentUser]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    if (!showProfileDrop) return;
    const fn = () => setShowProfileDrop(false);
    setTimeout(() => document.addEventListener('click', fn), 0);
    return () => document.removeEventListener('click', fn);
  }, [showProfileDrop]);

  useEffect(() => {
    if (cart.length === 0) navigate('/products');
  }, [cart, navigate]);

  const handleAuthSuccess = (user) => {
    setShowAuthModal(false);
    setFormData(p => ({
      ...p,
      name:  user.name  || p.name,
      phone: user.phone || p.phone,
      email: user.email || p.email,
    }));
    toast.success(`Welcome, ${user.name||'there'}! 🥛`);
  };

  const handleLogout = () => {
    logout();
    setShowProfileDrop(false);
    setFormData(p => ({...p, name:'', phone:'', email:''}));
    toast.success('Logged out 👋');
  };

  const validateForm = () => {
    const {name, email, phone, address, city, pincode} = formData;
    if (!name.trim())    { toast.error('Enter your full name');    return false; }
    if (!email.trim())   { toast.error('Enter your email');        return false; }
    if (!phone.trim())   { toast.error('Enter your phone number'); return false; }
    if (!address.trim()) { toast.error('Enter your address');      return false; }
    if (!city.trim())    { toast.error('Enter your city');         return false; }
    if (!pincode.trim()) { toast.error('Enter your pincode');      return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) { setShowAuthModal(true); return; }
    if (!validateForm()) return;

    setLoading(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { toast.error('Payment gateway failed to load.'); setLoading(false); return; }

      const options = {
        key:         RAZORPAY_KEY,
        amount:      Math.round(parseFloat(getCartTotal()) * 100),
        currency:    'INR',
        name:        'Fresh Hills',
        description: 'Home Delivery Order',
        image:       '/images/logo.png',
        prefill: {
          name:    formData.name,
          email:   formData.email,
          contact: formData.phone,
        },
        theme: { color: '#059669' },
        handler: async (rz) => {
          try {
            const res = await customerCheckout({
              ...formData,
              user_id:             currentUser.id,
              total_amount:        getCartTotal(),
              order_type:          'delivery',
              payment_method:      'razorpay',
              razorpay_payment_id: rz.razorpay_payment_id,
              items: cart.map(item => ({
                product_id: item.id,
                quantity:   item.quantity || 1,
                price:      item.price    || 0,
              })),
            });
            if (res.data.success) {
              clearCart();
              navigate(`/order-confirmation/${res.data.order_number}`);
              toast.success('Order placed! 🎉');
            } else {
              toast.error(res.data.message || 'Order save failed.');
            }
          } catch {
            toast.error('Payment done but order save failed. Payment ID: ' + rz.razorpay_payment_id);
          } finally {
            setLoading(false);
          }
        },
        modal: { ondismiss: () => { setLoading(false); toast('Payment cancelled.', {icon:'ℹ️'}); } },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        setLoading(false);
        toast.error('Payment failed: ' + (resp.error?.description || ''));
      });
      rzp.open();
    } catch {
      toast.error('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  if (cart.length === 0) return null;

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:'Nunito',sans-serif;background:#f9fafb;}

    /* ── HEADER (identical to Products) ── */
    .pr-hdr{position:sticky;top:0;z-index:50;background:rgba(255,255,255,.97);
            backdrop-filter:blur(12px);border-bottom:1px solid rgba(229,231,235,.6);
            transition:box-shadow .3s;width:100%;}
    .pr-hdr.sc{box-shadow:0 4px 20px rgba(0,0,0,.07);}
    .pr-hdr-in{display:flex;align-items:center;justify-content:space-between;
               height:54px;gap:.4rem;padding:0 .875rem;max-width:1280px;margin:0 auto;}
    @media(min-width:768px){.pr-hdr-in{height:64px;padding:0 1.5rem;}}
    .pr-logo{display:flex;align-items:center;cursor:pointer;flex-shrink:0;}
    .pr-logo img{height:34px;object-fit:contain;}
    @media(min-width:768px){.pr-logo img{height:42px;}}
    .pr-acts{display:flex;align-items:center;gap:.3rem;flex-shrink:0;}
    .pr-ib{width:34px;height:34px;display:flex;align-items:center;justify-content:center;
           border-radius:50%;border:none;background:#f3f4f6;color:#374151;cursor:pointer;
           transition:all .2s;flex-shrink:0;}
    .pr-ib:hover{background:#e5e7eb;color:#059669;}
    .pr-ib svg{width:1rem;height:1rem;}
    @media(min-width:768px){.pr-ib{width:38px;height:38px;}}
    .pr-cw{position:relative;display:inline-flex;}
    .pr-cb{position:absolute;top:-4px;right:-4px;width:.9rem;height:.9rem;background:#ef4444;
           color:white;font-size:.5rem;font-weight:800;border-radius:50%;
           display:flex;align-items:center;justify-content:center;border:2px solid white;}
    .pr-pw{position:relative;display:inline-flex;}
    .pr-pb{display:flex;align-items:center;gap:.3rem;background:#ecfdf5;
           border:1.5px solid #a7f3d0;color:#047857;font-weight:700;font-size:.75rem;
           padding:.28rem .65rem .28rem .28rem;border-radius:9999px;cursor:pointer;
           transition:all .2s;font-family:'Nunito',sans-serif;white-space:nowrap;}
    .pr-pb:hover{background:#d1fae5;}
    .pr-pav{width:1.5rem;height:1.5rem;background:linear-gradient(135deg,#10b981,#059669);
            border-radius:50%;display:flex;align-items:center;justify-content:center;
            color:white;font-size:.65rem;font-weight:800;flex-shrink:0;}
    .pr-pnm{display:none;max-width:65px;overflow:hidden;text-overflow:ellipsis;}
    @media(min-width:440px){.pr-pnm{display:block;}}
    .pr-lbtn{display:flex;align-items:center;gap:.28rem;background:white;color:#059669;
             border:1.5px solid #d1fae5;font-weight:700;font-size:.72rem;
             padding:.32rem .65rem;border-radius:9999px;cursor:pointer;
             transition:all .2s;white-space:nowrap;font-family:'Nunito',sans-serif;}
    .pr-lbtn:hover{background:#f0fdf4;}
    .pr-lbtn svg{width:.75rem;height:.75rem;}

    /* ── CHECKOUT LAYOUT ── */
    .co-wrap{max-width:1100px;margin:0 auto;padding:1.5rem .875rem;}
    @media(min-width:640px){.co-wrap{padding:2rem 1.5rem;}}
    .co-bk{display:inline-flex;align-items:center;gap:.4rem;background:none;border:none;
           color:#6b7280;font-weight:600;font-size:.85rem;cursor:pointer;
           padding:.4rem .75rem;border-radius:9999px;transition:all .2s;
           font-family:'Nunito',sans-serif;margin-bottom:1rem;}
    .co-bk:hover{background:#f3f4f6;color:#059669;}
    .co-bk svg{width:.9rem;height:.9rem;}
    .co-ttl{font-size:1.5rem;font-weight:900;
            background:linear-gradient(135deg,#059669,#0891b2);
            -webkit-background-clip:text;-webkit-text-fill-color:transparent;
            margin-bottom:1.35rem;display:flex;align-items:center;gap:.6rem;}
    @media(min-width:768px){.co-ttl{font-size:2rem;}}
    .co-grid{display:grid;gap:1.5rem;}
    @media(min-width:1024px){.co-grid{grid-template-columns:1.5fr 1fr;}}

    /* login prompt */
    .co-prompt{background:linear-gradient(135deg,#fff7ed,#fffbeb);border:1px solid #fed7aa;
               border-radius:1rem;padding:.9rem 1.1rem;display:flex;align-items:center;
               justify-content:space-between;flex-wrap:wrap;gap:.6rem;margin-bottom:1.25rem;}
    .co-prompt-txt{font-size:.85rem;color:#92400e;font-weight:700;display:flex;align-items:center;gap:.4rem;}
    .co-prompt-txt svg{width:1rem;height:1rem;}
    .co-prompt-btn{background:#059669;color:white;border:none;border-radius:9999px;
                   padding:.45rem 1.1rem;font-weight:700;font-size:.82rem;cursor:pointer;
                   font-family:'Nunito',sans-serif;transition:all .2s;}
    .co-prompt-btn:hover{background:#047857;}

    /* card */
    .co-card{background:white;border-radius:1.25rem;padding:1.5rem;
             box-shadow:0 4px 16px rgba(0,0,0,.04);border:1.5px solid #f3f4f6;}
    .co-card-ttl{font-size:1rem;font-weight:900;color:#111827;margin-bottom:1.1rem;
                 display:flex;align-items:center;gap:.5rem;}
    .co-card-ttl svg{width:1.1rem;height:1.1rem;color:#059669;}

    /* form */
    .co-fg2{display:grid;grid-template-columns:1fr;gap:.85rem;}
    @media(min-width:480px){.co-fg2{grid-template-columns:1fr 1fr;}}
    .co-lbl{display:block;font-size:.78rem;font-weight:700;color:#374151;margin-bottom:.35rem;}
    .co-inp{width:100%;padding:.65rem .9rem;border:2px solid #e5e7eb;border-radius:.75rem;
            font-size:.88rem;transition:border-color .2s;font-family:'Nunito',sans-serif;}
    .co-inp:focus{outline:none;border-color:#10b981;box-shadow:0 0 0 3px rgba(16,185,129,.1);}
    .co-ta{width:100%;padding:.65rem .9rem;border:2px solid #e5e7eb;border-radius:.75rem;
           font-size:.88rem;transition:border-color .2s;resize:vertical;
           font-family:'Nunito',sans-serif;}
    .co-ta:focus{outline:none;border-color:#10b981;box-shadow:0 0 0 3px rgba(16,185,129,.1);}

    /* razorpay banner */
    .co-rpay{display:flex;align-items:center;gap:.5rem;background:linear-gradient(135deg,#fff7ed,#fefce8);
             border:1px solid #fcd34d;border-radius:.85rem;padding:.65rem .85rem;
             margin-bottom:.85rem;font-size:.76rem;color:#78350f;font-weight:700;}

    /* place order btn */
    .co-btn{width:100%;padding:.9rem;background:linear-gradient(135deg,#059669,#10b981);
            color:white;border:none;border-radius:9999px;font-weight:800;font-size:1rem;
            cursor:pointer;transition:all .3s;display:flex;align-items:center;
            justify-content:center;gap:.5rem;box-shadow:0 4px 14px rgba(5,150,105,.25);
            font-family:'Nunito',sans-serif;margin-top:.5rem;}
    .co-btn:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 10px 20px rgba(5,150,105,.3);}
    .co-btn:disabled{opacity:.6;cursor:not-allowed;transform:none;}
    @keyframes spin{to{transform:rotate(360deg)}}
    .spin-sm{width:1.1rem;height:1.1rem;border:2px solid rgba(255,255,255,.4);
             border-top-color:white;border-radius:50%;animation:spin .7s linear infinite;display:inline-block;}

    /* summary */
    .co-sum-sticky{position:sticky;top:80px;}
    .co-sum-item{display:flex;justify-content:space-between;align-items:flex-start;
                 padding:.6rem 0;border-bottom:1px solid #f9fafb;font-size:.88rem;}
    .co-sum-item:last-child{border-bottom:none;}
    .co-sum-left{display:flex;gap:.65rem;flex:1;}
    .co-sum-img{width:38px;height:38px;border-radius:.55rem;background:#f9fafb;
                border:1.5px solid #f3f4f6;display:flex;align-items:center;
                justify-content:center;overflow:hidden;flex-shrink:0;}
    .co-sum-img img{width:100%;height:100%;object-fit:contain;}
    .co-sum-img span{font-size:1.1rem;}
    .co-sum-name{font-weight:700;color:#111827;margin-bottom:.12rem;font-size:.85rem;}
    .co-sum-qty{font-size:.72rem;color:#9ca3af;}
    .co-sum-price{font-weight:800;color:#059669;white-space:nowrap;font-size:.9rem;}
    .co-sum-tot{display:flex;justify-content:space-between;font-size:1.15rem;
                font-weight:900;color:#111827;margin-top:.75rem;}
    .co-sum-tot-amt{color:#059669;}
    .co-dlv{display:flex;align-items:center;gap:.4rem;background:#ecfdf5;
            padding:.65rem .85rem;border-radius:.65rem;margin-top:.85rem;
            font-size:.78rem;color:#065f46;font-weight:700;}
    .co-dlv svg{width:.9rem;height:.9rem;flex-shrink:0;}

    /* trust bar */
    .co-trust{background:#f9fafb;padding:1.35rem 0;border-top:1px solid #e5e7eb;margin-top:2.5rem;}
    .co-trust-in{max-width:1280px;margin:0 auto;padding:0 .875rem;
                 display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:.85rem 1.75rem;}
    .co-trust-item{display:flex;align-items:center;gap:.5rem;}
    .co-trust-ico{width:1.65rem;height:1.65rem;border-radius:50%;display:flex;align-items:center;justify-content:center;}
    .co-trust-item span{font-size:.82rem;font-weight:600;color:#374151;}

    /* footer */
    .co-footer{background:#0f172a;color:white;padding:3.5rem 0 2rem;}
    .co-footer-in{max-width:1280px;margin:0 auto;padding:0 .875rem;}
    .co-footer-grid{display:grid;grid-template-columns:1fr;gap:2rem;margin-bottom:2.5rem;}
    @media(min-width:640px){.co-footer-grid{grid-template-columns:repeat(2,1fr);}}
    @media(min-width:1024px){.co-footer-grid{grid-template-columns:2fr 1fr 1fr 1.5fr;gap:2.5rem;}}
    .co-flogo{display:flex;align-items:center;margin-bottom:1rem;cursor:pointer;}
    .co-flogo img{height:40px;object-fit:contain;}
    .co-fabout{color:#94a3b8;line-height:1.7;font-size:.88rem;margin-bottom:1rem;}
    .co-fsoc{display:flex;gap:.65rem;}
    .co-fsoc a{width:2rem;height:2rem;background:#1e293b;border-radius:.55rem;display:flex;
               align-items:center;justify-content:center;color:white;text-decoration:none;transition:all .3s;}
    .co-fsoc a:hover{background:#2563eb;transform:translateY(-2px);}
    .co-fttl{font-size:.95rem;font-weight:700;color:white;margin-bottom:1rem;display:flex;align-items:center;gap:.35rem;}
    .co-fttl svg{width:.9rem;height:.9rem;color:#ef4444;}
    .co-flinks{list-style:none;}
    .co-flinks li{margin-bottom:.6rem;}
    .co-flinks a{color:#94a3b8;text-decoration:none;font-size:.88rem;
                 transition:all .2s;display:inline-flex;align-items:center;gap:.3rem;}
    .co-flinks a:hover{color:white;transform:translateX(4px);}
    .co-fcontact{list-style:none;}
    .co-fcontact li{display:flex;align-items:flex-start;gap:.55rem;color:#94a3b8;font-size:.88rem;margin-bottom:.75rem;}
    .co-fcontact li svg{width:1rem;height:1rem;flex-shrink:0;margin-top:.1rem;}
    .co-fnl{margin-top:.85rem;}
    .co-fnl p{color:white;font-size:.88rem;font-weight:600;margin-bottom:.4rem;}
    .co-fnl-row{display:flex;gap:.35rem;}
    .co-fnl-row input{flex:1;padding:.55rem .85rem;border-radius:9999px;border:1px solid #334155;
                      background:#1e293b;color:white;font-size:.82rem;font-family:'Nunito',sans-serif;}
    .co-fnl-row input::placeholder{color:#64748b;}
    .co-fnl-row button{background:#2563eb;color:white;border:none;border-radius:9999px;
                       padding:.55rem .9rem;font-weight:700;font-size:.78rem;cursor:pointer;font-family:'Nunito',sans-serif;}
    .co-fbot{border-top:1px solid #1e293b;padding-top:1.75rem;display:flex;
             flex-direction:column;align-items:center;gap:.85rem;text-align:center;}
    @media(min-width:768px){.co-fbot{flex-direction:row;justify-content:space-between;text-align:left;}}
    .co-fcopy{color:#64748b;font-size:.78rem;}
    .co-fbot-links{display:flex;flex-wrap:wrap;gap:1.25rem;justify-content:center;}
    .co-fbot-links a{color:#94a3b8;text-decoration:none;font-size:.78rem;transition:color .2s;}
    .co-fbot-links a:hover{color:white;}
    .co-seo{color:#f59e0b !important;font-weight:700;display:inline-flex;align-items:center;gap:.3rem;}
    @keyframes hb{0%,100%{transform:scale(1)}25%,75%{transform:scale(1.15)}}
    .hb{animation:hb 1.4s ease-in-out infinite;}
  `;

  const Header = () => (
    <header className={`pr-hdr${scrolled?' sc':''}`}>
      <div className="pr-hdr-in">
        <div className="pr-logo" onClick={()=>navigate('/')}>
          <img src="/images/logo.png" alt="Fresh Hills"
            onError={e=>{e.target.onerror=null;e.target.src='https://via.placeholder.com/42?text=FH';}}/>
        </div>
        <div className="pr-acts">
          <div className="pr-cw">
            <button className="pr-ib" onClick={()=>navigate('/cart')}><ShoppingCartIcon/></button>
            {cartCount>0 && <span className="pr-cb">{cartCount>9?'9+':cartCount}</span>}
          </div>
          {isLoggedIn ? (
            <div className="pr-pw" onClick={e=>{e.stopPropagation();setShowProfileDrop(p=>!p);}}>
              <button className="pr-pb">
                <div className="pr-pav">{(currentUser.name||'U').charAt(0).toUpperCase()}</div>
                <span className="pr-pnm">{(currentUser.name||'').split(' ')[0]||currentUser.phone}</span>
              </button>
              {showProfileDrop && (
                <ProfileDropdown user={currentUser} onLogout={handleLogout}
                  onViewProfile={()=>{setShowProfileDrop(false);setShowProfile(true);}}/>
              )}
            </div>
          ) : (
            <button className="pr-lbtn" onClick={()=>setShowAuthModal(true)}>
              <UserIcon/>Login
            </button>
          )}
        </div>
      </div>
    </header>
  );

  const Footer = () => (
    <footer className="co-footer">
      <div className="co-footer-in">
        <div className="co-footer-grid">
          <div>
            <div className="co-flogo" onClick={()=>navigate('/')}>
              <img src="/images/logo.png" alt="Fresh Hills"
                onError={e=>{e.target.onerror=null;e.target.src='https://via.placeholder.com/40?text=FH';}}/>
            </div>
            <p className="co-fabout">Kerala's trusted nadan milk store. Pure cow milk, fresh paneer, curd and more — delivered to your door every morning since 2024.</p>
            <div className="co-fsoc">
              {['fb','tw','ig'].map(s=>(
                <a key={s} href="#"><svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width:'1rem',height:'1rem'}}><circle cx="12" cy="12" r="10" strokeWidth="2"/></svg></a>
              ))}
            </div>
          </div>
          <div>
            <h4 className="co-fttl"><HeartIcon/> Quick Links</h4>
            <ul className="co-flinks">
              {[['/','/Home'],['/products','Products'],['/#offers','Offers'],['/#contact','Contact']].map(([h,l])=>(
                <li key={l}><a href={h}><ChevronRightIcon style={{width:'.7rem'}}/>{l}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="co-fttl"><HeartIcon/> Our Products</h4>
            <ul className="co-flinks">
              {['Fresh Milk','Paneer','Curd & Yogurt','Buttermilk','Ghee'].map(p=>(
                <li key={p}><a href="/products"><ChevronRightIcon style={{width:'.7rem'}}/>{p}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="co-fttl"><HeartIcon/> Contact Us</h4>
            <ul className="co-fcontact">
              <li><MapPinIcon/><span>36/845, Fesicon Enterprises, Near Sunrise Hospital, Sea Port - Air Port Road, Kakkanad, Ernakulam, Kerala, 682021</span></li>
              <li><DevicePhoneMobileIcon/><span>+91 70108 44160</span></li>
              <li>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width:'1rem',height:'1rem',flexShrink:0}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                <span>hello@fesiconenterprises.com</span>
              </li>
            </ul>
            <div className="co-fnl">
              <p>Subscribe for offers</p>
              <div className="co-fnl-row">
                <input type="email" placeholder="Your email address"/>
                <button>Go</button>
              </div>
            </div>
          </div>
        </div>
        <div className="co-fbot">
          <div className="co-fcopy">
            <span>© {currentYear} FESICON ENTERPRISE. All rights reserved. ❤️ Made in Kerala</span>
            <br/><span style={{fontSize:'.7rem',display:'block',marginTop:'.2rem'}}>GST: 32AAGFF6427Q1Z2</span>
          </div>
          <div className="co-fbot-links">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
            <a href="https://seo9ine.com" target="_blank" rel="noopener noreferrer" className="co-seo">
              SEO9INE <HeartIcon className="hb" style={{width:'.85rem',height:'.85rem',color:'#ef4444'}}/>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );

  return (
    <>
      <style>{css}</style>
      {showAuthModal && <WhatsAppAuthModal onSuccess={handleAuthSuccess} onClose={()=>setShowAuthModal(false)}/>}
      {showProfile   && <CustomerProfile  onClose={()=>setShowProfile(false)}/>}

      <div style={{minHeight:'100vh',background:'#f9fafb'}}>
        <Header/>

        <div className="co-wrap">
          <button className="co-bk" onClick={()=>navigate('/cart')}>
            <ArrowLeftIcon/>Back to Cart
          </button>
          <h1 className="co-ttl">🛒 Checkout</h1>

          {/* Login prompt for guests */}
          {!isLoggedIn && (
            <div className="co-prompt">
              <span className="co-prompt-txt">
                <ShieldCheckIcon/>Login with WhatsApp to place your order
              </span>
              <button className="co-prompt-btn" onClick={()=>setShowAuthModal(true)}>
                Login with WhatsApp 📲
              </button>
            </div>
          )}

          <div className="co-grid">
            {/* LEFT: Delivery form */}
            <div>
              <div className="co-card">
                <div className="co-card-ttl"><TruckIcon/>Delivery Information</div>
                <form onSubmit={handleSubmit} style={{display:'flex',flexDirection:'column',gap:'.85rem'}}>
                  <div className="co-fg2">
                    <div>
                      <label className="co-lbl">Full Name *</label>
                      <input className="co-inp" type="text" placeholder="Your full name"
                        value={formData.name} onChange={e=>setFormData(p=>({...p,name:e.target.value}))} required/>
                    </div>
                    <div>
                      <label className="co-lbl">Email *</label>
                      <input className="co-inp" type="email" placeholder="your@email.com"
                        value={formData.email} onChange={e=>setFormData(p=>({...p,email:e.target.value}))} required/>
                    </div>
                  </div>
                  <div>
                    <label className="co-lbl">Phone *</label>
                    <input className="co-inp" type="tel" placeholder="10-digit mobile number"
                      value={formData.phone} onChange={e=>setFormData(p=>({...p,phone:e.target.value}))} required/>
                  </div>
                  <div>
                    <label className="co-lbl">Delivery Address *</label>
                    <textarea className="co-ta" rows="3"
                      placeholder="House/Flat No., Building, Street, Landmark"
                      value={formData.address} onChange={e=>setFormData(p=>({...p,address:e.target.value}))} required/>
                  </div>
                  <div className="co-fg2">
                    <div>
                      <label className="co-lbl">City *</label>
                      <input className="co-inp" type="text" placeholder="City"
                        value={formData.city} onChange={e=>setFormData(p=>({...p,city:e.target.value}))} required/>
                    </div>
                    <div>
                      <label className="co-lbl">Pincode *</label>
                      <input className="co-inp" type="text" placeholder="6-digit pincode"
                        value={formData.pincode} onChange={e=>setFormData(p=>({...p,pincode:e.target.value}))} required/>
                    </div>
                  </div>

                  <div className="co-rpay">
                    🔒 Secured by <strong style={{color:'#059669',margin:'0 3px'}}>Razorpay</strong> — UPI, Cards, Net Banking &amp; Wallets
                  </div>

                  <button type="submit" className="co-btn" disabled={loading}>
                    {loading
                      ? <><span className="spin-sm"/>Processing…</>
                      : !isLoggedIn
                        ? '🔐 Login to Place Order'
                        : `✅ Pay ₹${getCartTotal()} via Razorpay`}
                  </button>
                </form>
              </div>
            </div>

            {/* RIGHT: Order summary */}
            <div className="co-sum-sticky">
              <div className="co-card">
                <div className="co-card-ttl"><ShoppingCartIcon/>Order Summary</div>
                <div style={{maxHeight:'18rem',overflowY:'auto'}}>
                  {cart.map(item => {
                    const imgSrc = getImageSrc(item);
                    const showImg = imgSrc && !imgErrors[item.id];
                    return (
                      <div key={item.id} className="co-sum-item">
                        <div className="co-sum-left">
                          <div className="co-sum-img">
                            {showImg
                              ? <img src={imgSrc} alt={item.name}
                                  onError={()=>setImgErrors(p=>({...p,[item.id]:true}))}
                                  style={{width:'100%',height:'100%',objectFit:'contain'}}/>
                              : <span>{getEmoji(item)}</span>}
                          </div>
                          <div>
                            <div className="co-sum-name">{item.name}</div>
                            <div className="co-sum-qty">Qty: {item.quantity||1}</div>
                          </div>
                        </div>
                        <span className="co-sum-price">₹{(item.price||0)*(item.quantity||1)}</span>
                      </div>
                    );
                  })}
                </div>
                <hr style={{border:'none',borderTop:'2px solid #f3f4f6',margin:'.65rem 0'}}/>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:'.5rem',fontSize:'.88rem'}}>
                  <span style={{color:'#6b7280'}}>Subtotal</span><span style={{fontWeight:600}}>₹{getCartTotal()}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:'.5rem',fontSize:'.88rem'}}>
                  <span style={{color:'#6b7280'}}>Delivery</span>
                  <span style={{color:'#10b981',fontWeight:700}}>FREE</span>
                </div>
                <div className="co-sum-tot">
                  <span>Total</span>
                  <span className="co-sum-tot-amt">₹{getCartTotal()}</span>
                </div>
                <div className="co-dlv"><TruckIcon/>Free delivery · Est. 30–45 min</div>
                <div style={{display:'flex',alignItems:'center',gap:'.4rem',marginTop:'.65rem',fontSize:'.75rem',color:'#9ca3af'}}>
                  <ShieldCheckIcon style={{width:'.9rem',height:'.9rem'}}/>100% secure · Razorpay
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust bar */}
        <div className="co-trust">
          <div className="co-trust-in">
            {[{bg:'#d1fae5',ico:<BoltIcon style={{width:'.9rem',height:'.9rem',color:'#059669'}}/>,label:'Same Day Delivery'},
              {bg:'#dbeafe',ico:<CurrencyRupeeIcon style={{width:'.9rem',height:'.9rem',color:'#2563eb'}}/>,label:'Cashless Payment'},
              {bg:'#fef3c7',ico:<CubeIcon style={{width:'.9rem',height:'.9rem',color:'#d97706'}}/>,label:'Fresh Stock Daily'},
              {bg:'#fae8ff',ico:<UserGroupIcon style={{width:'.9rem',height:'.9rem',color:'#9333ea'}}/>,label:'10k+ Customers'},
            ].map(t=>(
              <div key={t.label} className="co-trust-item">
                <div className="co-trust-ico" style={{background:t.bg}}>{t.ico}</div>
                <span>{t.label}</span>
              </div>
            ))}
          </div>
        </div>

        <Footer/>
      </div>
    </>
  );
};

export default Checkout;